const ORG_ID_KEY = 'organizationId';
const ORGS_KEY = 'organizations';

// --- In-tab change notification --------------------------------------------
// The browser's `storage` event only fires in *other* tabs, so mutating the
// org state from this tab must notify local subscribers explicitly.
type Listener = () => void;
const listeners = new Set<Listener>();

const emitOrgChange = () => listeners.forEach((l) => l());

export const subscribeOrgChange = (listener: Listener): (() => void) => {
    listeners.add(listener);
    const onStorage = (e: StorageEvent) => {
        if (e.key === ORG_ID_KEY || e.key === ORGS_KEY || e.key === null) {
            orgsCache = null; // another tab changed it — drop the cached snapshot
            listener();
        }
    };
    window.addEventListener("storage", onStorage);
    return () => {
        listeners.delete(listener);
        window.removeEventListener("storage", onStorage);
    };
};

// --- Current organization ---------------------------------------------------

export const getOrgId = (): string | null => {
    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');
    if (queryStart !== -1) {
        const id = new URLSearchParams(hash.slice(queryStart)).get("organization_id");
        if (id) {
            localStorage.setItem(ORG_ID_KEY, id);
            return id;
        }
    }
    return localStorage.getItem(ORG_ID_KEY);
};

export const setOrgId = (id: string): void => {
    localStorage.setItem(ORG_ID_KEY, id);
    addOrganization(id); // emits the change
};

export const clearOrgId = (): void => {
    localStorage.removeItem(ORG_ID_KEY);
    emitOrgChange();
};

// --- Organization history ---------------------------------------------------
// Cached snapshot so useSyncExternalStore gets a stable reference between changes.
let orgsCache: string[] | null = null;

const readOrganizations = (): string[] => {
    const raw = localStorage.getItem(ORGS_KEY);
    if (raw) {
        try {
            const list = JSON.parse(raw);
            if (Array.isArray(list))
                return list.filter((x): x is string => typeof x === "string");
        } catch {
            // fall through and reconstruct from the current organization
        }
    }
    const current = getOrgId();
    const initial = current ? [current] : [];
    localStorage.setItem(ORGS_KEY, JSON.stringify(initial));
    return initial;
};

// Organizations the user has signed into before. When the key is absent, it is
// constructed from the current organization id.
export const getOrganizations = (): string[] => {
    if (orgsCache === null) orgsCache = readOrganizations();
    return orgsCache;
};

export const addOrganization = (id: string): void => {
    const list = getOrganizations().filter((o) => o !== id);
    list.unshift(id);
    orgsCache = list;
    localStorage.setItem(ORGS_KEY, JSON.stringify(list));
    emitOrgChange();
};

export const getOrgServerBaseUrl = (): string =>
    process.env.REACT_APP_ORG_SERVER_URL || "https://fleetapi.r2c2.ai";
